// `user.tutorial`: `GET user` carries a `tutorial` field and
// `PUT user/tutorial` writes it.
//
// Before the backend shipped it the transport answered from localStorage
// (`tutorial-mock:<userId>`). A browser that still holds that key has its
// progress merged into the server's on the next read (`mergeMockTutorial`),
// and the key goes only once the server has it.
//
// It is not `hint_show_count`. That list counts coachmark impressions and is
// written by every hint; reusing it would make the tutorial's durable state a
// side effect of an unrelated feature.

import { request } from '@/api/apiUtils';
import type { ResponseType, TutorialChapterId, TutorialCoreState, UserTutorial, UserTutorialPatch } from '@/types';

export const TUTORIAL_CHAPTER_IDS: readonly TutorialChapterId[] = ['browse', 'structure', 'notes', 'visibility', 'ai'];

/** A user the backend has no tutorial state for. */
export const EMPTY_USER_TUTORIAL: UserTutorial = {
    core: null,
    chapters: [],
    project_id: null,
    offer: { snoozed_until: null, dismiss_count: 0 },
};

/**
 * `user.tutorial` as the client may rely on it, from whatever the server sent.
 *
 * Field by field: a value the client does not understand falls back to that
 * field's empty state and the rest is kept. A stale chapter id from a later
 * client, or a missing `offer`, must not erase a finished core.
 */
export function parseUserTutorial(raw: unknown): UserTutorial {
    const record = asRecord(raw);
    if (record === null) return EMPTY_USER_TUTORIAL;
    const offer = asRecord(record.offer);
    return {
        core: readCore(record.core),
        chapters: readChapters(record.chapters),
        project_id: readId(record.project_id),
        offer: {
            snoozed_until: typeof offer?.snoozed_until === 'string' ? offer.snoozed_until : null,
            dismiss_count: readCount(offer?.dismiss_count),
        },
    };
}

/** What `PUT user/tutorial` leaves behind: the patch's top-level fields
 *  replace the stored ones (`offer` whole), the rest stays. */
export function applyUserTutorialPatch(current: UserTutorial, patch: UserTutorialPatch): UserTutorial {
    return parseUserTutorial({ ...current, ...patch });
}

const CORE_STRENGTH = { done: 2, skipped: 1 } as const;
const coreStrength = (core: TutorialCoreState) => (core === null ? 0 : CORE_STRENGTH[core]);

const laterSnooze = (a: string | null, b: string | null) => {
    if (a === null || b === null) return a ?? b;
    return Date.parse(b) > Date.parse(a) ? b : a;
};

/**
 * The server's state with the old mock's progress carried in, field by field:
 * the stronger `core` (done > skipped > null), the union of chapters, the
 * server's project when it has one, the later snooze, the higher dismiss
 * count. Idempotent, so a carry-over that half failed can run again.
 *
 * Two different practice projects keep the server's; the mock's project is
 * the user's own and is left alone.
 */
export function mergeMockTutorial(server: UserTutorial, mock: UserTutorial): UserTutorial {
    return parseUserTutorial({
        core: coreStrength(mock.core) > coreStrength(server.core) ? mock.core : server.core,
        chapters: [...server.chapters, ...mock.chapters],
        project_id: server.project_id ?? mock.project_id,
        offer: {
            snoozed_until: laterSnooze(server.offer.snoozed_until, mock.offer.snoozed_until),
            dismiss_count: Math.max(server.offer.dismiss_count, mock.offer.dismiss_count),
        },
    });
}

/** Where the localStorage mock kept a user's state, before ticket 13. */
const mockStorageKey = (userId: number) => `tutorial-mock:${userId}`;

/**
 * `GET user` → `tutorial`, and `PUT user/tutorial`. `userId` names the old
 * mock's storage key only; the requests are the current user's by token.
 *
 * Both answers pass through the carry-over, so a write made while a failed
 * carry-over still holds the key answers the merge too, not the server's
 * state without the mock's progress.
 */
export const userTutorialTransport = {
    async get(userId: number): Promise<UserTutorial> {
        const response: ResponseType<{ tutorial?: unknown }> = await request('user');
        return withCarryOver(userId, parseUserTutorial(response.data.tutorial));
    },
    async put(userId: number, patch: UserTutorialPatch): Promise<UserTutorial> {
        return withCarryOver(userId, await putRemote(patch));
    },
};

async function putRemote(patch: UserTutorialPatch): Promise<UserTutorial> {
    const response: ResponseType<unknown> = await request('user/tutorial', { method: 'PUT', body: JSON.stringify(patch) });
    return parseUserTutorial(response.data);
}

/** While the old mock key exists: writes the merge, then drops the key. A
 *  failed write keeps the key for the next read or write and still answers
 *  the merge, so an offer dismissed in the mock doesn't come back meanwhile. */
async function withCarryOver(userId: number, server: UserTutorial): Promise<UserTutorial> {
    const mock = readMock(userId);
    if (mock === null) return server;
    const merged = mergeMockTutorial(server, mock);
    // Both went through `parseUserTutorial`, so their key order is the same.
    if (JSON.stringify(merged) === JSON.stringify(server)) {
        localStorage.removeItem(mockStorageKey(userId));
        return server;
    }
    try {
        const stored = await putRemote(merged);
        localStorage.removeItem(mockStorageKey(userId));
        return stored;
    } catch {
        return merged;
    }
}

function readMock(userId: number): UserTutorial | null {
    const stored = localStorage.getItem(mockStorageKey(userId));
    if (stored === null) return null;
    try {
        return parseUserTutorial(JSON.parse(stored));
    } catch {
        return EMPTY_USER_TUTORIAL;
    }
}

function asRecord(value: unknown): Record<string, unknown> | null {
    return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function readCore(value: unknown): TutorialCoreState {
    return value === 'done' || value === 'skipped' ? value : null;
}

function readChapters(value: unknown): TutorialChapterId[] {
    if (!Array.isArray(value)) return [];
    return TUTORIAL_CHAPTER_IDS.filter(id => value.includes(id));
}

/** The server writes the id as a number; a numeric string is tolerated. */
function readId(value: unknown): number | null {
    const id = typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : value;
    return typeof id === 'number' && Number.isInteger(id) && id > 0 ? id : null;
}

function readCount(value: unknown): number {
    return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : 0;
}
